

































































































import { AdvancedSelect, DescriptionListCard, ShortTypeBadge, TailwindTable, TwSelect } from '@/app/components';
import { useAxios } from '@/app/composable';
import { S } from '@/app/utilities';
import { computed, defineComponent, onMounted, PropType, ref } from '@vue/composition-api';
import { SelfBuildingSquareSpinner } from 'epic-spinners';
import { isNil } from 'ramda';
import { RetrievalAPI } from '../../api';
import { AssetTypeId } from '../../constants';
import { Asset } from '../../types';
import { RetrievalPayload } from './../../types/data-profiling.type';

export default defineComponent({
    name: 'AssetTable',
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        columns: {
            type: Array as PropType<{ name: string; type?: string }[]>,
            default: () => [],
        },
    },
    components: {
        TailwindTable,
        TwSelect,
        ShortTypeBadge,
        DescriptionListCard,
        SelfBuildingSquareSpinner,
        AdvancedSelect,
    },
    setup(props: any, { root, emit }: { root: any; emit: any }) {
        const { exec, loading } = useAxios(true);

        // Number of rows
        const numOfRowsOptions = ref<{ option: number }[]>([{ option: 25 }, { option: 50 }, { option: 100 }]);
        const numOfRows = ref<number>(numOfRowsOptions.value[0].option);

        // Position
        const positionOptions = ref<{ option: string }[]>([{ option: 'top' }, { option: 'bottom' }]);
        const position = ref<'top' | 'bottom'>('top');
        const previousPosition = ref<'top' | 'bottom'>('top');

        // Columns
        const columnOptions = computed(() =>
            props.columns.map((col: { name: string; type?: string }) => ({
                key: col.name,
                label: col.name,
                type: col.type ?? '',
            })),
        );
        const selectedColumns = ref<string[]>(
            props.columns.slice(0, 5).map((col: { name: string; type?: string }) => col.name),
        );

        const sortBy = ref<{ field: string; asc: boolean } | null>(null);
        const data = ref<any[]>([]);
        const page = ref<number>(1);
        const pageSize = ref<number>(10);

        const tableColumnsMap = computed(() =>
            data.value.length
                ? Object.keys(data.value[0]).reduce((acc: any, key: string) => {
                      acc[key.replace(/\./g, '_')] = key;
                      return acc;
                  }, {})
                : {},
        );

        const tableData = computed(() => {
            const newData = data.value.reduce((acc: any[], row: any) => {
                const newRow: any = {};
                Object.keys(row).forEach((key) => {
                    newRow[key.replace(/\./g, '_')] = row[key];
                });
                acc.push(newRow);
                return acc;
            }, []);
            return position.value === 'top' ? newData : newData.reverse();
        });

        const paginatedData = computed(() =>
            tableData.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value),
        );

        const tableColumns = computed(() =>
            selectedColumns.value.map((key) => ({
                key,
                label: key,
                type: props.columns.find((col: { name: string; type?: string }) => col.name === key)?.type ?? '',
                sortable: { enabled: true, key: tableColumnsMap.value[key], clearable: true },
            })),
        );

        const totalData = computed(() => data.value.length);

        const pagination = computed(() => ({
            page: page.value,
            pageSize: pageSize.value,
            total: totalData.value,
        }));

        const retrievalPayload = ref<RetrievalPayload>({
            assetId: props.asset.id,
            configuration: { datasets: [{ id: `${props.asset.id}`, fields: [] }] },
            params: { pageSize: numOfRows.value, flatten: props.asset.assetTypeId === AssetTypeId.Dataset },
        });

        const fetchAssetTable = () => {
            exec(RetrievalAPI.getMongoData(retrievalPayload.value))
                .then((res: any) => {
                    data.value = res.data ? res.data.results : [];
                })
                .catch((e) => {
                    if (e.response.status !== 403) throw e; // if the error is not for forbidden access, then send to sentry
                    (root as any).$toastr.e('You do not have access to view the specific asset.', 'Access Forbidden!');
                    emit('back-to-assets');
                });
        };

        const setSortBy = () => {
            if (!isNil(sortBy.value)) {
                const order = position.value === 'top' ? sortBy.value.asc : !sortBy.value.asc;
                retrievalPayload.value.params.orderBy = sortBy.value.field;
                retrievalPayload.value.params.orderDirection = order ? 'ASC' : 'DESC';
            } else {
                retrievalPayload.value.params.orderBy = '_id';
                retrievalPayload.value.params.orderDirection = position.value === 'top' ? 'ASC' : 'DESC';
            }
        };

        const changeSortBy = (newSortBy: { field: string; asc: boolean } | null) => {
            sortBy.value = newSortBy;
            setSortBy();
            fetchAssetTable();
        };

        const changePage = (newPage: number) => {
            page.value = newPage;
        };

        const changeLimit = (limit: number) => {
            if (retrievalPayload.value.params.pageSize === limit) return;
            retrievalPayload.value.params.pageSize = limit;
            fetchAssetTable();
            changePage(1);
        };

        const changePosition = () => {
            if (previousPosition.value === position.value) return;
            previousPosition.value = position.value;
            setSortBy();
            fetchAssetTable();
            changePage(1);
        };

        onMounted(() => {
            fetchAssetTable();
        });

        return {
            S,
            numOfRowsOptions,
            numOfRows,
            position,
            positionOptions,
            selectedColumns,
            columnOptions,
            sortBy,
            loading,
            paginatedData,
            tableColumns,
            pagination,
            changeSortBy,
            changePage,
            changeLimit,
            changePosition,
        };
    },
});
